// applog parser — the one that DOESN'T parse.
//
// Every other parser in this directory reconstructs structure out of a rendered
// text line. accur8-parser.ts is the clearest case:
//
//   const ACCUR8_LOG_PATTERN = /^(\d{2}):(\d{2}):(\d{2})[.,](\d{3})\s*\|\s*(\w+)\s*\|.../
//
// That regex is digging the level, thread, category and timestamp back out of a
// string — the very fields the WRITER computed at the log call and then destroyed
// by Sprintf'ing them together. It is brittle by construction: a line that does not
// match is silently absorbed as a "continuation" of the previous entry, so a format
// drift degrades into quietly-wrong grouping rather than a visible error.
//
// The `applog` channel ends that. level, caller, thread/goid, the event timestamp,
// the MDC and the exception are FIELDS on the wire (continuum_rpc.LogRecord), emitted
// identically by Go (godev/applog) and the JVM (sync's ApplogAppender) and pinned by a
// cross-language golden. So there is nothing to parse: this is a field mapping.
//
// Two things it surfaces that no text parser could:
//   - the exception as a CHAIN (message + concrete type per link, down to the root
//     cause), instead of a wall of stack trace jammed into one line
//   - RECORD LOSS: the writer's spool overflowing (`droppedRecords`). A truncated log
//     that admits what it lost beats one that silently shows a hole.

import { LogRecord, LogError, LogLevel } from '../../../hermesclient/proto/continuum_rpc';
import { LogLevel as ViewerLogLevel } from './types';

/** One link of a rendered exception chain, ready for the UI. */
export interface LogErrorLink {
  message: string;
  /** The concrete type — "java.io.IOException", "*net.OpError". A FIELD, so it is filterable. */
  type: string;
  /** Rendered frames for this link; may be empty. */
  stack: string;
}

/**
 * Flatten a LogError's `cause` chain into links, ROOT LAST.
 *
 * The chain is the whole reason `cause` is a link rather than flattened prose: the
 * root cause — the interesting one — sits at the far end, and a human should not have
 * to read a wall of text to reach it.
 *
 * Bounded: a cyclic chain is pathological, but a log viewer must not hang on one.
 */
const MAX_ERROR_CHAIN = 32;

export function flattenErrorChain(err: LogError | undefined): LogErrorLink[] {
  const links: LogErrorLink[] = [];
  let cur: LogError | undefined = err;
  for (let i = 0; cur && i < MAX_ERROR_CHAIN; i++) {
    links.push({
      message: cur.message ?? '',
      type: cur.type ?? '',
      stack: cur.stack ?? '',
    });
    cur = cur.cause;
  }
  return links;
}

/** The wire LogLevel enum -> the viewer's level strings. */
export function toViewerLevel(level: LogLevel | undefined): ViewerLogLevel | undefined {
  switch (level) {
    case LogLevel.trace: return 'TRACE';
    case LogLevel.debug: return 'DEBUG';
    case LogLevel.info:  return 'INFO';
    case LogLevel.warn:  return 'WARN';
    case LogLevel.error: return 'ERROR';
    default: return undefined; // level_unspecified / UNRECOGNIZED — do not invent one
  }
}

/**
 * The concurrent-context identity, as ONE display string.
 *
 * `thread` (a name: "main", "nats-worker-3") is the JVM's; `goid` (a number) is Go's.
 * Exactly one is populated — each language fills its own and neither fakes nor
 * stringifies the other's — so the viewer shows whichever is present.
 *
 * goid arrives as a STRING (ts_proto forceLong=string on the 64-bit fields), so an
 * absent goid is proto3's default "0" — which is TRUTHY. Compare against the zero
 * value, not truthiness, or every JVM record picks up a bogus thread of "0".
 */
export function toThread(rec: LogRecord): string | undefined {
  if (rec.thread) return rec.thread;
  const goid = String(rec.goid ?? '');
  if (goid && goid !== '0') return goid;
  return undefined;
}

/** Everything the viewer needs from one applog record. */
export interface ApplogEntry {
  message: string;
  originalLine: string;
  timestamp: Date;
  level?: ViewerLogLevel;
  /** Go: "a8nats.go:816". JVM: the logger name. Same question, each language's idiom. */
  category?: string;
  thread?: string;
  /** The MDC / structured-logging bag. */
  metadata?: Record<string, string>;
  /** The exception, root last. Empty when the record carries no error. */
  errorChain: LogErrorLink[];
  /** > 0 when the WRITER's spool overflowed before this record (NATS was unreachable). */
  droppedRecords: number;
}

/**
 * Map one LogRecord onto the viewer's shape. No regex, no continuation heuristic,
 * no guessing — every value is read from the field the writer put it in.
 *
 * `eventTime` is Buffer.timestamp: the moment the log CALL happened, not when the
 * batch shipped. A spool that drained after a NATS outage must not stamp every record
 * with the moment the outage ended, which is why the writer captures it at emit.
 */
export function applogRecordToEntry(rec: LogRecord, eventTime: Date): ApplogEntry {
  const errorChain = flattenErrorChain(rec.error);

  // `rendered` is the writer's own pre-formatted line, shipped alongside the fields
  // during rollout so an un-updated reader still prints something correct. It is the
  // compatibility shim, NOT the source of truth — prefer it for the raw column only.
  const originalLine = rec.rendered || rec.message || '';

  return {
    message: rec.message ?? '',
    originalLine,
    timestamp: eventTime,
    level: toViewerLevel(rec.level),
    category: rec.caller || undefined,
    thread: toThread(rec),
    metadata:
      rec.extraData && Object.keys(rec.extraData).length > 0 ? rec.extraData : undefined,
    errorChain,
    // Also a 64-bit field, so also a string on the wire. A drop COUNT is small —
    // convert to the number the UI counts with.
    droppedRecords: Number(rec.droppedRecords ?? 0),
  };
}

/**
 * Render an exception chain as indented text, root last — the same shape
 * `a8 logs --structured` prints, so the CLI and the UI tell the same story.
 *
 * Used for the message body when a record carries an error; a richer collapsible
 * rendering can layer on top of `ApplogEntry.errorChain` without changing this.
 */
export function renderErrorChain(links: LogErrorLink[]): string {
  const out: string[] = [];
  links.forEach((link, i) => {
    const label = i === 0 ? 'error:' : 'caused by:';
    out.push(link.type ? `${label} ${link.message} (${link.type})` : `${label} ${link.message}`);
    if (link.stack) {
      for (const line of link.stack.replace(/\n+$/, '').split('\n')) {
        out.push(`    ${line}`);
      }
    }
  });
  return out.join('\n');
}
